// src/components/LoginScreen.jsx — gates the whole app behind Supabase Auth.
import { useState } from 'react'
import { supabase } from '../db/index.js'
import { t } from '../lib/i18n.js'
import { Icon } from './Icon.jsx'

export function LoginScreen() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [err,      setErr]      = useState('')
  const [busy,     setBusy]     = useState(false)

  async function submit(e) {
    e.preventDefault()
    if (!username.trim() || !password) return
    setBusy(true); setErr('')
    // Usernames map to the synthetic email `${username}@sentavus.com`.
    const u = username.trim().toLowerCase()
    const email = u.includes('@') ? u : `${u}@sentavus.com`

    // ── Core is the authentication authority (2026-07-30) ─────────────────────
    // We ask the server, which gates on CORE and hands back a real scheduler
    // session. setSession makes it indistinguishable from signInWithPassword, so
    // every RLS read, x-viewer-token call and onAuthStateChange listener is
    // unchanged — this file is the only one that had to move.
    //
    // need_property:false is load-bearing: the Desk never touches the property
    // project, and two active agents have no vaulted property credential. Asking
    // for one would lock them out of a surface that does not need it.
    try {
      const r = await fetch('https://api.sentavus.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, include_scheduler: true, need_property: false }),
      })
      const j = await r.json().catch(() => ({}))

      if (j.scheduler_token && j.scheduler_refresh_token) {
        const { error } = await supabase.auth.setSession({
          access_token:  j.scheduler_token,
          refresh_token: j.scheduler_refresh_token,
        })
        if (!error) return   // auth listener flips the gate
      }

      // A refusal the CORE GATE owns — resigned, revoked, or simply wrong. Never
      // fall through to a direct sign-in here: that is the one path that would
      // hand the Desk back to someone core just turned away.
      if (j.gate === 'core') { setErr(t('login_error')); setBusy(false); return }
      // Review 2026-09-23: the server ANSWERED, so its refusal stands. 403 = the revoked /
      // resigned doors (the legacy path's 403s carry no gate marker), 503 = the core gate
      // failing CLOSED ("Login temporarily unavailable") — falling through on either reopened
      // exactly the door the server just shut. A 502/504 is nginx with the app DOWN, i.e. a
      // real outage, and keeps the fallback. Still falls through: a 2xx without a
      // scheduler session (admin/staff accounts with no roster row — the legacy path hands
      // back property tokens only, so this is how they have always signed in), and a bare
      // 401 (legacy identity whose property/core credential differs; resigned ROSTER agents
      // never reach it — core_auth denies them with gate:'core'). The direct sign-in below
      // still enforces the scheduler-side ban.
      if (r.status === 403 || r.status === 503) { setErr(t('login_error')); setBusy(false); return }
    } catch {
      // Server unreachable — fall through. The direct sign-in below still enforces
      // the scheduler-side ban, which is what lock-agent has always set.
    }

    // Identities core cannot gate (admin accounts with no roster row) and any
    // server outage land here — the original direct sign-in, unchanged.
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) { setErr(t('login_error')); setBusy(false) }
    // On success, the auth listener flips the gate — no further action here.
  }

  const inp = 'w-full rounded-lg border border-input px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
      <form onSubmit={submit} className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        {/* Same mark and capitalisation as the app header (owner, 2026-07-30): senTavus,
            capital T. The old "S" tile and "Sentavus" were a second, wrong brand. */}
        <div className="flex items-center gap-2.5">
          <img src="/logo.svg" alt="senTavus" className="h-8 w-auto" />
          <div className="font-semibold text-sm tracking-tight">senTavus</div>
        </div>

        <div className="space-y-2.5">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">{t('login_username')}</label>
            <input value={username} onChange={e => { setUsername(e.target.value); setErr('') }}
              autoFocus autoCapitalize="none" autoCorrect="off" className={inp} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">{t('login_password')}</label>
            {/* Reveal toggle: a wrong-credentials refusal is indistinguishable from a typo,
                and the agent cannot see which one they made. */}
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={password}
                onChange={e => { setPassword(e.target.value); setErr('') }}
                className={`${inp} pr-10`} />
              <button type="button" tabIndex={-1}
                onClick={() => setShowPw(s => !s)}
                aria-label={t(showPw ? 'login_hide_password' : 'login_show_password')}
                title={t(showPw ? 'login_hide_password' : 'login_show_password')}
                className={`absolute inset-y-0 right-0 px-3 flex items-center transition-colors ${showPw ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                <Icon name="eye" size={16} />
              </button>
            </div>
          </div>
        </div>

        {err && <div className="text-xs text-destructive">{err}</div>}

        <button type="submit" disabled={busy || !username.trim() || !password}
          className="w-full rounded-lg bg-primary text-primary-foreground text-sm font-semibold py-2.5 hover:opacity-90 transition-opacity disabled:opacity-50">
          {busy ? t('login_signing_in') : t('login_sign_in')}
        </button>
      </form>
    </div>
  )
}
